import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "cartTable", "cartSpinner", "cartCount", "totalAmount", 
    "checkoutButton", "cartRow", "errorMessage", "parcelError",
    "checkoutContent", "contactMeModal", "successModal",
    "promoCodePreDiscountAmount", "promoCodeDiscountAmount"
  ]

  connect() {
    this.quantityInputPrevFocusValue = null
    this.quantityInputFocusedInterval = null
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Setup quantity update listeners
    this.element.querySelectorAll(".cart-quantity").forEach(input => {
      input.addEventListener('change', (e) => this.handleQuantityChange(e))
      input.addEventListener('focus', (e) => this.handleQuantityFocus(e))
    })

    // Setup form submit prevention
    this.element.querySelectorAll(".edit_order_item").forEach(form => {
      form.addEventListener('submit', (e) => e.preventDefault())
    })

    // Setup remove buttons
    this.element.querySelectorAll(".cart-remove").forEach(button => {
      button.addEventListener('click', (e) => this.handleDelete(e))
      button.addEventListener('touchstart', (e) => this.handleDelete(e))
    })

    // Setup checkout button
    if (this.hasCheckoutButtonTarget) {
      this.checkoutButtonTarget.addEventListener('click', (e) => this.handleCheckoutClick(e))
      this.checkoutButtonTarget.addEventListener('touchstart', (e) => this.handleCheckoutClick(e))
    }

    // Setup modal close buttons
    const contactMeCloseButton = document.getElementById('contact-me-close-button')
    if (contactMeCloseButton) {
      contactMeCloseButton.addEventListener('click', () => this.closeContactMeModal())
      contactMeCloseButton.addEventListener('touchstart', () => this.closeContactMeModal())
    }

    // Setup order form submission
    const orderSubmitButton = document.getElementById('order-submit-button')
    if (orderSubmitButton) {
      this.setupOrderFormSubmission(orderSubmitButton)
    }

    // Setup promo code
    const promoCodePlaceholder = document.getElementById("promo-code-placeholder")
    if (promoCodePlaceholder) {
      promoCodePlaceholder.addEventListener('click', () => this.showPromoCodeForm())
    }
  }

  handleQuantityChange(e) {
    if (e.target.value <= 1) {
      e.target.value = 1
    }
    this.updateQuantity(e)
  }

  handleQuantityFocus(e) {
    this.quantityInputPrevFocusValue = e.target.value
    this.quantityInputFocusedInterval = setInterval(() => this.checkUpdateOrderItemQuantity(e), 500)
  }

  checkUpdateOrderItemQuantity(e) {
    const orderItemId = e.target.dataset.id
    const activeElement = document.activeElement

    if (activeElement?.dataset?.id === orderItemId) {
      if (e.target.value !== this.quantityInputPrevFocusValue) {
        this.quantityInputPrevFocusValue = e.target.value
        if (e.target.value !== "") {
          this.updateQuantity(e)
        }
      }
    } else {
      clearInterval(this.quantityInputFocusedInterval)
    }
  }

  async updateQuantity(e) {
    e.preventDefault()

    const orderItemId = e.target.dataset.id
    const updateForm = document.getElementById(`edit_api_models_order_item_${orderItemId}`)

    try {
      const response = await fetch(updateForm.action, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json'
        },
        body: new URLSearchParams(new FormData(updateForm))
      })
      const data = await response.json()

      if (this.hasTotalAmountTarget) {
        this.totalAmountTarget.innerHTML = data.orderSubtotal
      }
      document.getElementById(`cart-total-price-${orderItemId}`).innerHTML = data.orderItemTotal
      document.getElementById(`cart-row-price-${data.orderItemId}`).innerHTML = `$${data.orderItemPrice.toFixed(2)}`
      document.getElementById(`error-${orderItemId}`).classList.remove('-visible')
      
      if (this.hasCheckoutButtonTarget) {
        this.checkoutButtonTarget.disabled = false
      }

      if (data.discountAmount) {
        if (this.hasPromoCodePreDiscountAmountTarget) {
          this.promoCodePreDiscountAmountTarget.innerHTML = data.orderSubtotal
        }
        if (this.hasPromoCodeDiscountAmountTarget) {
          this.promoCodeDiscountAmountTarget.innerHTML = `$${data.discountAmount.toFixed(2)}`
        }
        if (this.hasTotalAmountTarget) {
          this.totalAmountTarget.innerHTML = `$${(Number(data.orderSubtotal.replace(/[^0-9\.-]+/g,"")) - data.discountAmount).toFixed(2)}`
        }
      }
    } catch (error) {
      if (!error.responseJSON) {
        Sentry?.captureMessage('Error updating item in cart: ' + error)
      }
      const quantityError = error.responseJSON?.errors?.quantity?.[0]?.replace(',is not a number', '')
      if (quantityError) {
        const errorElement = document.getElementById(`error-${orderItemId}`)
        errorElement.innerHTML = 'Quantity ' + quantityError
        errorElement.classList.add('-visible')
        if (this.hasCheckoutButtonTarget) {
          this.checkoutButtonTarget.disabled = true
        }
      }
    }
  }

  async handleDelete(e) {
    e.preventDefault()

    const orderItemId = e.target.dataset.id
    const title = e.target.dataset.title

    if (confirm(`Are you sure you want to delete '${title}' from your cart?`)) {
      if (this.hasCartTableTarget) {
        this.cartTableTarget.style.display = 'none'
      }
      if (this.hasCartSpinnerTarget) {
        this.cartSpinnerTarget.style.display = 'block'
      }

      try {
        const response = await fetch(`/order_items/${orderItemId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json'
          }
        })
        const data = await response.json()

        if (this.hasCartTableTarget) {
          this.cartTableTarget.style.display = 'block'
        }
        if (this.hasCartSpinnerTarget) {
          this.cartSpinnerTarget.style.display = 'none'
        }

        const itemCount = data.itemCount
        if (this.hasCartCountTarget) {
          this.cartCountTarget.innerHTML = itemCount
        }
        if (this.hasTotalAmountTarget) {
          this.totalAmountTarget.innerHTML = data.orderSubtotal
        }
        
        document.getElementById(`cart-row-${orderItemId}`).remove()

        if (itemCount === 0) {
          if (this.hasCheckoutButtonTarget) {
            this.checkoutButtonTarget.style.display = 'none'
          }
        }

        if (data.allSameOrigin === true) {
          if (this.hasCheckoutButtonTarget) {
            this.checkoutButtonTarget.dataset.allSameOrigin = "true"
          }
        }

        // Handle parcel shipping validation error
        this.handleParcelShippingError(data.parcelShippingError)

        if (data.discountAmount) {
          if (this.hasPromoCodePreDiscountAmountTarget) {
            this.promoCodePreDiscountAmountTarget.innerHTML = data.orderSubtotal
          }
          if (this.hasPromoCodeDiscountAmountTarget) {
            this.promoCodeDiscountAmountTarget.innerHTML = `$${data.discountAmount.toFixed(2)}`
          }
          if (this.hasTotalAmountTarget) {
            this.totalAmountTarget.innerHTML = `$${(parseFloat(data.orderSubtotal.slice(1)) - data.discountAmount).toFixed(2)}`
          }
        }
      } catch (error) {
        alert("There was an error removing this item from the cart.")
        if (this.hasCartSpinnerTarget) {
          this.cartSpinnerTarget.innerHTML = "Please refresh the page."
        }
        Sentry?.captureMessage('Error deleting item from cart: ' + error)
      }
    }
  }

  handleParcelShippingError(parcelShippingError) {
    if (parcelShippingError) {
      // Update checkout button to disabled state
      if (this.hasCheckoutButtonTarget) {
        this.checkoutButtonTarget.dataset.parcelError = "true"
        this.checkoutButtonTarget.disabled = true
        this.checkoutButtonTarget.style.opacity = "0.5"
        this.checkoutButtonTarget.style.cursor = "not-allowed"
      }
      
      // Show or update the error message
      if (this.hasParcelErrorTarget) {
        const errorSpan = this.parcelErrorTarget.querySelector('span')
        if (errorSpan) {
          errorSpan.textContent = parcelShippingError
        }
        this.parcelErrorTarget.classList.remove('hidden')
      } else {
        // Create new error div if it doesn't exist
        const errorHtml = `
          <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert" data-shopping-cart-target="parcelError">
            <strong class="font-bold">Shipping Notice:</strong>
            <span class="block sm:inline">${parcelShippingError}</span>
          </div>
        `
        if (this.hasCheckoutContentTarget) {
          const firstHeading = this.checkoutContentTarget.querySelector('.checkout-subheading')
          if (firstHeading) {
            firstHeading.insertAdjacentHTML('beforebegin', errorHtml)
          }
        }
      }
    } else {
      // Remove error and re-enable button if no validation error
      if (this.hasParcelErrorTarget) {
        this.parcelErrorTarget.remove()
      }
      if (this.hasCheckoutButtonTarget) {
        this.checkoutButtonTarget.dataset.parcelError = "false"
        this.checkoutButtonTarget.disabled = false
        this.checkoutButtonTarget.style.opacity = ""
        this.checkoutButtonTarget.style.cursor = ""
      }
    }
  }

  handleCheckoutClick(e) {
    // Check for parcel shipping validation error
    if (this.hasCheckoutButtonTarget && this.checkoutButtonTarget.dataset.parcelError === "true") {
      e.preventDefault()
      alert("Please resolve the shipping issue before proceeding to checkout. Add more items to exceed 165 lbs total weight, select seeds from a different origin, or contact support for assistance.")
      return
    }
    
    if (this.hasCheckoutButtonTarget && this.checkoutButtonTarget.dataset.allSameOrigin === "false") {
      e.preventDefault()
      if (this.hasContactMeModalTarget) {
        this.contactMeModalTarget.classList.remove('hidden')
      }
    }
  }

  closeContactMeModal() {
    if (this.hasContactMeModalTarget) {
      this.contactMeModalTarget.classList.add('hidden')
    }
  }

  showSuccessModal() {
    if (this.hasSuccessModalTarget) {
      this.successModalTarget.classList.remove('hidden')

      const successCloseButton = document.getElementById('success-close-button')
      if (successCloseButton) {
        successCloseButton.addEventListener('click', () => {
          successCloseButton.addEventListener('touchstart', () => {
            this.successModalTarget.classList.add('hidden')
            if (location.pathname.includes("/cart")) {
              location = "/"
            }
          })
        })
      }
    }
  }

  setupOrderFormSubmission(orderSubmitButton) {
    const orderId = orderSubmitButton.dataset.id
    const orderForm = document.getElementById(`edit_api_models_order_${orderId}`)

    orderSubmitButton.addEventListener('click', async (event) => {
      event.preventDefault()

      document.querySelector('.contact-me-error-container').style.display = 'none'

      try {
        const response = await fetch(orderForm.action, {
          method: 'PUT',
          headers: {
            'Accept': 'application/json'
          },
          body: new URLSearchParams(new FormData(orderForm))
        })
        const data = await response.json()

        orderForm.reset()

        if (!window.current_admin_user) {
          if (window.gtag) {
            gtag('event', 'RequestedShipping', {
              event_category: 'Lead',
              currency: 'USD',
              value: 10.00
            })
          }
          if (window.fbq) {
            fbq('track', 'InitiateCheckout', {
              currency: 'USD',
              value: 10.00
            })
          }
        }

        if (data.redirect_url) {
          window.location = data.redirect_url
        }
      } catch (error) {
        const errorJson = error.responseJSON
        let errorHtml = []

        if (errorJson?.errors?.full_messages) {
          errorJson.errors.full_messages.forEach(error => {
            errorHtml.push(`<li>${error}</li>`)
          })
        } else {
          errorHtml.push('<li>Oops! Something went wrong on our end. Please contact us for support</li>')
        }

        document.querySelector('.contact-me-errors').innerHTML = errorHtml.join('')
        document.querySelector('.contact-me-error-container').style.display = 'block'
      }
    })
  }

  showPromoCodeForm() {
    const promoCodePlaceholder = document.getElementById("promo-code-placeholder")
    if (promoCodePlaceholder) {
      promoCodePlaceholder.style.display = 'none'
    }
    const promoCodeForm = document.getElementById("promo-code-form")
    if (promoCodeForm) {
      promoCodeForm.classList.remove("hidden")
    }
    const promoCodeError = document.querySelector(".promo-code-error")
    if (promoCodeError) {
      promoCodeError.style.display = 'none'
    }
  }
}